import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, tap } from 'rxjs/operators';
import { Customer } from '../../shared/models/customer';
import { CustomerAddress } from '../../shared/models/customer-address';
import { ServiceItem } from '../../shared/models/service-item';
import { SalesOrder, SyncSalesOrderResult } from '../../shared/models/sales-order';
import { SalesPerson } from '../../shared/models/sales-person';
import { ApiResponse } from '../../shared/models/api-response';
import { firstValueFrom } from 'rxjs';
import { QueryStringHelper } from '../../shared/helpers/query-string.helper';
import { PrintTemplate } from '../../shared/models/print-template';
import { SalesStore } from '../../shared/models/sales-store';
import { SyncStatus } from '../../shared/models/offline-metadata';
import { ReplicationSyncStatus } from '../../shared/models/replication-sync-status';
import { SALES_ORDERS_V25_COLLECTION } from './sales-order-replication.service';
import { query, where, orderBy, limit, updateDoc } from '@angular/fire/firestore';
import { SalesBundle } from 'src/app/shared/models/sales-bundle';
import { ServiceResult } from 'src/app/shared/models/service-result';
import { Tag } from 'src/app/shared/models/tag';
import { Promotion, PromotionStatus } from 'src/app/shared/models/promotion';
import { PagedList } from 'src/app/shared/models/paged-list';
import { CustomerQuery, PromotionQuery } from 'src/app/shared/models/query';
import { CashRegister } from 'src/app/shared/models/cash-register';
import { AppSettingsService, FirestoreService, OnlineOfflineService } from '@core';
import { AuthService } from '@auth';
import { CustomerV3, DexieDbProvider, SalesOrderV3, SOLockAutomaticActions } from '@shared';
import { SystemSettingsService } from './system-settings.service';
import { Product } from 'src/app/point-of-sale/models/product';
import { PrintTemplateV3 } from '@shared/models/print-template-v3';
import { ImageService } from '@core/services/image.service';
import { convertToDate } from '@shared/helpers/convert-to-date';
import { flatMap, uniq } from 'lodash';
import { PosLocation } from 'src/app/point-of-sale/models/pos-location';
import { CustomerSyncResult, CustomerV3SyncStatus } from '@core/models/customer-sync-result';
import { Tax } from 'src/app/point-of-sale/models/tax.model';
import { DeliveryZone } from 'src/app/point-of-sale/models/delivery-zone';
import { CustomField } from '@shared/models/custom-field';

@Injectable({
  providedIn: 'root'
})
export class CorePosService {
  baseUrl = '';
  edaraCoreBaseUrl = '';

  constructor(
    private authService: AuthService,
    private httpClient: HttpClient,
    private firestoreService: FirestoreService,
    private localDbProvider: DexieDbProvider,
    private onlineOfflineService: OnlineOfflineService,
    private imageService: ImageService,
    private systemSettingsService: SystemSettingsService
  ) {
    this.baseUrl = AppSettingsService.appSettings.edaraApi.baseUrl;
    this.edaraCoreBaseUrl = AppSettingsService.appSettings.edaraCoreApi.baseUrl;
    if (this.edaraCoreBaseUrl.endsWith('/')) {
      this.edaraCoreBaseUrl = this.edaraCoreBaseUrl.slice(0, -1);
    }
  }

  async loadAllCustomers(): Promise<ServiceResult<Customer[]>> {
    const query = {
      offset: 0,
      limit: 1000
    };
    const serviceResult = new ServiceResult<Customer[]>();
    const count = await this.countAllCustomersLocalAsync();
    if (!this.onlineOfflineService.isOnline || count > 0) {
      query.limit = count;
      serviceResult.statusCode = 200;
      serviceResult.result = await this.getCustomersLocalAsync(query);
      serviceResult.totalCount = count;
      return serviceResult;
    } else {
      let response: ApiResponse<Customer[]> | undefined;
      let customers: Customer[] = [];
      do {
        response = await this.getCustomersAsync(query);
        if (response && response.status_code === 200) {
          query.offset += query.limit;
          customers = customers.concat(response.result);
          this.addCustomersToLocalDb(response.result);
        }
      } while (response && response.status_code === 200 && response.total_count > query.offset);
      serviceResult.statusCode = response?.status_code;
      serviceResult.result = customers;
      serviceResult.statusMessage = response?.error_message;
      serviceResult.totalCount = response?.total_count;
      return serviceResult;
    }
  }

  private countAllCustomersLocalAsync(): Promise<number> {
    return this.localDbProvider.db.customers.count()
      .catch((err: any) => this.localDbProvider.handleErrors(err));
  }

  addCustomersToLocalDb(customers: Customer[]) {
    if (customers && customers.length > 0) {
      this.deleteOfflineCustomers(customers);
      customers.forEach(customer => this.mapCustomer(customer));
      this.localDbProvider.db.customers.bulkPut(customers)
        .catch((err: any) => this.localDbProvider.handleErrors(err));
    }
  }

  deleteOfflineCustomers(customers: Customer[]) {
    const offlineCustomers = customers.filter(customer => customer.external_id);
    for (let index = 0; index < offlineCustomers.length; index++) {
      const element = offlineCustomers[index];
      if (element.external_id) {
        this.deleteCustomerByExternalIdFromLocalDb(element.external_id);
      }
    };
  }

  deleteCustomerByExternalIdFromLocalDb(externalId: string) {
    this.localDbProvider.db.customers
      .where('external_id').equals(externalId)
      .delete()
      .catch((err: any) => this.localDbProvider.handleErrors(err));
  }

  getCustomersAsync(query: any): Promise<ApiResponse<Customer[]> | undefined> {
    const queryString = new QueryStringHelper().toQueryString(query);
    return firstValueFrom(
      this.httpClient.get<ApiResponse<Customer[]>>(this.baseUrl + 'v2.0/customers?' + queryString)
        .pipe(map(res => {
          if (res.result) {
            res.result.forEach(customer => this.mapCustomer(customer));
          }
          return res;
        }))
    ).catch(() => { return undefined; });
  }

  private async getCustomersLocalAsync(query: any): Promise<Customer[]> {
    return this.getCustomersQueryFn(query)
      .offset(query.offset)
      .limit(query.limit)
      .toArray();
  }

  private getCustomersQueryFn(query: any) {
    const queryFn = this.localDbProvider.db.customers.orderBy('id')
      .filter((itm: Customer) => !query.code || itm.code?.includes(query.code))
      .and((itm: Customer) => !query.name || itm.name.includes(query.name))
      .and((itm: Customer) => !query.phone || itm.phone?.includes(query.phone))
      .and((itm: Customer) => !query.mobile || itm.mobile?.includes(query.mobile))
      .and((itm: Customer) => !query.email || itm.email?.includes(query.email));

    if (query.syncStatus === SyncStatus.Synced)
      queryFn.and((itm: Customer) => !itm.syncMetadata);
    else if (query.syncStatus === SyncStatus.Pending)
      queryFn.and((itm: Customer) => itm.syncMetadata);

    return queryFn;
  }

  addCustomerToLocalDb(customer: Customer) {
    if (customer) {
      this.mapCustomer(customer);
      this.localDbProvider.db.customers.put(customer)
        .catch((err: any) => this.localDbProvider.handleErrors(err));
    }
  }

  updateCustomersSyncStatus(salesOrders: SalesOrder[], syncSalesOrderResults: SyncSalesOrderResult[]) {
    if (salesOrders?.length && syncSalesOrderResults?.length) {
      syncSalesOrderResults.forEach(syncResult => {
        // Only process successfully synced orders
        if (syncResult.customer_id && syncResult.status_code === true) {
          const salesOrder = salesOrders.find(so => so.external_id === syncResult.external_id);
          if (salesOrder?.customer?.syncMetadata) {
            this.saveSyncedCustomerToLocalDb(syncResult.customer_id, salesOrder.customer);
          }
        }
      });
    }
  }

  async saveSyncedCustomerToLocalDb(customerId: number, customer: Customer) {
    if (customer.external_id) {
      this.deleteCustomerByExternalIdFromLocalDb(customer.external_id);
    }
    customer.id = customerId;
    // Fetch addresses from server to get real IDs assigned by the backend
    const addressesRes = await this.getCustomerAddresses(customerId);
    if (addressesRes && addressesRes.status_code === 200) {
      customer.customer_addresses = addressesRes.result;
    }
    delete customer.syncMetadata;
    this.addCustomerToLocalDb(customer);
  }

  getCustomerAddresses(customerId: number): Promise<ApiResponse<CustomerAddress[]> | undefined> {
    return firstValueFrom(
      this.httpClient.get<ApiResponse<CustomerAddress[]>>(this.baseUrl + `v2.0/customers/addresses/${customerId}`)
    ).catch(() => { return undefined; });
  }

  private mapCustomer(customer: Customer) {
    if (customer.mobile && customer.mobile.trim().includes(' ')) {
      customer.mobile_parts = customer.mobile.split(' ').filter(itm => !!itm);
    }
  }

  getServiceItemsAsync(query: any): Promise<ApiResponse<ServiceItem[]> | undefined> {
    const queryString = new QueryStringHelper().toQueryString(query);
    return firstValueFrom(
      this.httpClient.get<ApiResponse<ServiceItem[]>>(this.baseUrl + 'v2.0/serviceItems?' + queryString)
        .pipe(map(res => {
          if (res.result) {
            res.result.forEach(serviceItem => this.mapServiceItem(serviceItem));
          }
          return res;
        }))
    ).catch(() => { return undefined; });
  }

  addServiceItemsToLocalDb(serviceItems: ServiceItem[]) {
    if (serviceItems && serviceItems.length > 0) {
      serviceItems.forEach(serviceItem => this.mapServiceItem(serviceItem));
      this.localDbProvider.db.serviceItems.bulkAdd(serviceItems)
        .catch((err: any) => this.localDbProvider.handleErrors(err));
    }
  }

  searchServiceItemsServer(searchKeyWords: string) {
    return this.httpClient.get<ApiResponse<ServiceItem[]>>(this.baseUrl + `v2.0/serviceItems/SearchServiceItems/${searchKeyWords}`)
      .pipe(map((res: ApiResponse<ServiceItem[]>) => {
        if (res.result) {
          this.addServiceItemsToLocalDb(res.result);
          return res.result;
        }
        return [];
      }));
  }

  private mapServiceItem(serviceItem: ServiceItem) {
    if (serviceItem.description && serviceItem.description.trim().includes(' ')) {
      serviceItem.description_keywords = serviceItem.description.split(' ').filter(itm => !!itm);
    }
  }

  approveSalesOrder(salesOrderCode: string): Promise<ApiResponse<any> | undefined> {
    return firstValueFrom(
      this.httpClient.post<ApiResponse<any>>(this.baseUrl + 'v2.0/salesOrders/Approve', { SalesOrder_code: salesOrderCode })
    ).catch(() => { return undefined; });
  }

  issueSalesOrder(salesOrderCode: string): Promise<ApiResponse<any> | undefined> {
    return firstValueFrom(
      this.httpClient.post<ApiResponse<any>>(this.baseUrl + 'v2.0/salesOrders/Issue', { SalesOrder_code: salesOrderCode })
    ).catch(() => { return undefined; });
  }

  async processAutomaticActionsForLockedOrders(syncSalesOrdersResult: SyncSalesOrderResult[]) {
    if (!syncSalesOrdersResult || syncSalesOrdersResult.length === 0) {
      return;
    }

    try {
      const soLockSettings = await this.systemSettingsService.getSOLockSettings();
      if (!soLockSettings || !soLockSettings.isLockEnabled) {
        return;
      }

      const autoApprove = soLockSettings.automaticActions === SOLockAutomaticActions.AutoApprove;
      const autoApproveAndIssue = soLockSettings.automaticActions === SOLockAutomaticActions.AutoApproveAndIssue;

      if (!autoApprove && !autoApproveAndIssue) {
        return;
      }

      for (const syncResult of syncSalesOrdersResult) {
        if (syncResult.status_code !== true || !syncResult.document_code) {
          continue;
        }

        try {
          const firestoreOrder = await this.firestoreService
            .getDocValueAsync<SalesOrder>(`${SALES_ORDERS_V25_COLLECTION}/${syncResult.external_id}`);

          if (!firestoreOrder || !firestoreOrder.is_locked || syncResult.require_auto_workorder) {
            continue;
          }

          // The automatic actions are due only while they are still outstanding. Without this
          // guard a re-sync of an already processed order would approve and issue it again.
          if (autoApprove && firestoreOrder.is_approved) {
            continue;
          }

          if (autoApproveAndIssue && firestoreOrder.is_approved && firestoreOrder.is_issued) {
            continue;
          }

          if (autoApprove) {
            await this.processAutoApprove(syncResult.document_code, syncResult.external_id);
          } else if (autoApproveAndIssue) {
            await this.processAutoApproveAndIssue(syncResult.document_code, syncResult.external_id, firestoreOrder.is_approved === true);
          }
        } catch (error) {
          console.error(`Error processing automatic actions for order ${syncResult.external_id}:`, error);
        }
      }
    } catch (error) {
      console.error('Error in processAutomaticActionsForLockedOrders:', error);
    }
  }

  private async processAutoApprove(documentCode: string, externalId: string) {
    const approveResponse = await this.approveSalesOrder(documentCode);

    if (approveResponse && approveResponse.status_code === 200) {
      await updateDoc(
        this.firestoreService.doc(`${SALES_ORDERS_V25_COLLECTION}/${externalId}`),
        { is_approved: true, approval_error: null }
      );
    } else {
      await updateDoc(
        this.firestoreService.doc(`${SALES_ORDERS_V25_COLLECTION}/${externalId}`),
        { approval_error: approveResponse?.error_message || 'Approval failed' }
      );
    }
  }

  private async processAutoApproveAndIssue(documentCode: string, externalId: string, alreadyApproved = false) {
    const approveResponse = alreadyApproved
      ? { status_code: 200 } as ApiResponse<any>
      : await this.approveSalesOrder(documentCode);

    if (approveResponse && approveResponse.status_code === 200) {
      if (!alreadyApproved) {
        await updateDoc(
          this.firestoreService.doc(`${SALES_ORDERS_V25_COLLECTION}/${externalId}`),
          { is_approved: true, approval_error: null }
        );
      }

      const issueResponse = await this.issueSalesOrder(documentCode);

      if (issueResponse && issueResponse.status_code === 200) {
        await updateDoc(
          this.firestoreService.doc(`${SALES_ORDERS_V25_COLLECTION}/${externalId}`),
          { is_issued: true, issue_error: null }
        );
      } else {
        await updateDoc(
          this.firestoreService.doc(`${SALES_ORDERS_V25_COLLECTION}/${externalId}`),
          { issue_error: issueResponse?.error_message || 'Issue failed' }
        );
      }
    } else {
      await updateDoc(
        this.firestoreService.doc(`${SALES_ORDERS_V25_COLLECTION}/${externalId}`),
        { approval_error: approveResponse?.error_message || 'Approval failed' }
      );
    }
  }

  async getAllUnsyncedSalesOrders(): Promise<SalesOrder[]> {
    const queryFn = query(this.firestoreService.col<SalesOrder>(SALES_ORDERS_V25_COLLECTION),
      where('metadata.organizationInfo.id', '==', this.authService.currentTenant),
      where('metadata.syncInfo.syncStatus', '!=', ReplicationSyncStatus.Synced),
      orderBy('metadata.syncInfo.syncStatus'),
      orderBy('document_date', 'desc')
    );

    const result = await this.firestoreService.getCollectionAsync<SalesOrder>(queryFn);

    return result.map(so => this.mapFirestoreDocumentToSalesOrder(so));
  }

  async loadAllPrintTemplates(): Promise<ServiceResult<PrintTemplate[]>> {
    const serviceResult = new ServiceResult<PrintTemplate[]>();
    const count = await this.countPrintTemplatesLocalAsync();
    if (!this.onlineOfflineService.isOnline || count > 0) {
      serviceResult.statusCode = 200;
      serviceResult.result = await this.getPrintTemplatesLocalAsync();
      serviceResult.totalCount = count;
      return serviceResult;
    } else {
      const query = {
        offset: 0,
        limit: 1000
      };
      let response: ApiResponse<PrintTemplate[]> | undefined;
      let printTemplates: PrintTemplate[] = [];
      do {
        response = await this.getPrintTemplatesAsync(query);
        if (response && response.status_code === 200) {
          query.offset += query.limit;
          printTemplates = printTemplates.concat(response.result);
          this.addPrintTemplatesToLocalDb(response.result);
        }
      } while (response && response.status_code === 200 && response.total_count > query.offset);
      serviceResult.statusCode = response?.status_code;
      serviceResult.result = printTemplates;
      serviceResult.statusMessage = response?.error_message;
      serviceResult.totalCount = response?.total_count;
      return serviceResult;
    }
  }

  addPrintTemplatesToLocalDb(printTemplates: PrintTemplate[]) {
    this.localDbProvider.db.printTemplates.bulkPut(printTemplates)
      .catch((err: any) => this.localDbProvider.handleErrors(err));
  }

  getPrintTemplatesAsync(query: any): Promise<ApiResponse<PrintTemplate[]> | undefined> {
    const queryString = new QueryStringHelper().toQueryString(query);
    return firstValueFrom(
      this.httpClient.get<ApiResponse<PrintTemplate[]>>(this.baseUrl + 'v2.0/salesOrders/PrintTemplates?' + queryString)
    ).catch(() => { return undefined; });
  }

  private countPrintTemplatesLocalAsync(): Promise<number> {
    return this.localDbProvider.db.printTemplates.count()
      .catch((err: any) => this.localDbProvider.handleErrors(err));
  }

  private getPrintTemplatesLocalAsync(): Promise<PrintTemplate[]> {
    return this.localDbProvider.db.printTemplates.toArray();
  }

  private mapFirestoreDocumentToSalesOrder(so: SalesOrder) {
    so.document_date = convertToDate(so.document_date);
    so.salesOrder_installments.map(installment => {
      installment.due_date = convertToDate(installment.due_date);
      return installment;
    });
    if (so.metadata?.createdDate) {
      so.metadata.createdDate = convertToDate(so.metadata.createdDate);
    }
    return so;
  }

  async loadAllSalesPersons(): Promise<ServiceResult<SalesPerson[]>> {
    const query = {
      offset: 0,
      limit: 1000
    };
    const serviceResult = new ServiceResult<SalesPerson[]>();
    const count = await this.countSalesPersonsLocalAsync();
    if (!this.onlineOfflineService.isOnline || count > 0) {
      query.limit = count;
      serviceResult.statusCode = 200;
      serviceResult.result = await this.getSalesPersonsLocalAsync(query);
      serviceResult.totalCount = count;
      return serviceResult;
    } else {
      let response: ApiResponse<SalesPerson[]> | undefined;
      let salesPersons: SalesPerson[] = [];
      do {
        response = await this.getSalesPersonsAsync(query);
        if (response && response.status_code === 200) {
          query.offset += query.limit;
          salesPersons = salesPersons.concat(response.result);
          this.addSalesPersonsToLocalDb(response.result);
        }
      } while (response && response.status_code === 200 && response.total_count > query.offset);
      serviceResult.statusCode = response?.status_code;
      serviceResult.result = salesPersons;
      serviceResult.statusMessage = response?.error_message;
      serviceResult.totalCount = response?.total_count;
      return serviceResult;
    }
  }

  addSalesPersonsToLocalDb(salesPersons: SalesPerson[]) {
    if (salesPersons && salesPersons.length > 0) {
      this.localDbProvider.db.salesPersons.bulkPut(salesPersons)
        .catch((err: any) => this.localDbProvider.handleErrors(err));
    }
  }

  getSalesPersonsAsync(query: any): Promise<ApiResponse<SalesPerson[]> | undefined> {
    const queryString = new QueryStringHelper().toQueryString(query);
    return firstValueFrom(
      this.httpClient.get<ApiResponse<SalesPerson[]>>(this.baseUrl + 'v2.0/Sales/UserSalesPersons?' + queryString)
    ).catch(() => { return undefined; });
  }

  private countSalesPersonsLocalAsync(): Promise<number> {
    return this.localDbProvider.db.salesPersons.count()
      .catch((err: any) => this.localDbProvider.handleErrors(err));
  }

  private getSalesPersonsLocalAsync(query: any): Promise<SalesPerson[]> {
    return this.localDbProvider.db.salesPersons
      .offset(query.offset)
      .limit(query.limit)
      .toArray();
  }

  getSalesStoresAsync(query: any): Promise<ApiResponse<SalesStore[]> | undefined> {
    const queryString = new QueryStringHelper().toQueryString(query);
    return firstValueFrom(
      this.httpClient.get<ApiResponse<SalesStore[]>>(this.baseUrl + 'v2.0/salesStores?' + queryString)
    ).catch(() => { return undefined; });
  }

  addSalesStoresToLocalDb(salesStores: SalesStore[]) {
    if (salesStores && salesStores.length > 0) {
      this.localDbProvider.db.salesStores.bulkAdd(salesStores)
        .catch((err: any) => this.localDbProvider.handleErrors(err));
    }
  }

  getSalesBundlesAsync(query: any): Promise<ApiResponse<SalesBundle[]> | undefined> {
    const queryString = new QueryStringHelper().toQueryString(query);
    return firstValueFrom(
      this.httpClient.get<ApiResponse<SalesBundle[]>>(this.baseUrl + 'v2.0/salesBundles?' + queryString)
        .pipe(map(res => {
          if (res.result) {
            res.result.forEach(salesBundle => this.mapSalesBundle(salesBundle));
          }
          return res;
        }))
    ).catch(() => { return undefined; });
  }

  addSalesBundlesToLocalDb(salesBundles: SalesBundle[]) {
    if (salesBundles && salesBundles.length > 0) {
      salesBundles.forEach(salesBundle => this.mapSalesBundle(salesBundle));
      this.localDbProvider.db.salesBundles.bulkAdd(salesBundles)
        .catch((err: any) => this.localDbProvider.handleErrors(err));
    }
  }

  searchSalesBundlesServer(searchKeyWords: string) {
    return this.httpClient.get<ApiResponse<SalesBundle[]>>(this.baseUrl + `v2.0/salesBundles/Search/${searchKeyWords}`)
      .pipe(map((res: ApiResponse<SalesBundle[]>) => {
        if (res.result) {
          this.addSalesBundlesToLocalDb(res.result);
          return res.result;
        }
        return [];
      }));
  }

  private mapSalesBundle(salesBundle: SalesBundle) {
    if (salesBundle.description && salesBundle.description.trim().includes(' ')) {
      salesBundle.description_keywords = salesBundle.description.split(' ').filter(itm => !!itm);
    }
  }

  addTagsToLocalDb(tags: Tag[]) {
    if (tags && tags.length > 0) {
      this.localDbProvider.db.tags.bulkPut(tags)
        .catch((err: any) => this.localDbProvider.handleErrors(err));
    }
  }

  getTagsAsync(query: any): Promise<ApiResponse<Tag[]> | undefined> {
    const queryString = new QueryStringHelper().toQueryString(query);
    return firstValueFrom(
      this.httpClient.get<ApiResponse<Tag[]>>(this.baseUrl + 'v2.0/common/tags?' + queryString)
    ).catch(() => { return undefined; });
  }

  addPromotionsToLocalDb(promotions?: Promotion[]) {
    if (promotions && promotions.length > 0) {
      this.localDbProvider.db.POS_Promotions.bulkPut(promotions)
        .catch((err: any) => this.localDbProvider.handleErrors(err));
    }
  }

  clearPromotionsFromLocalDb() {
    this.localDbProvider.db.POS_Promotions.clear();
  }

  getCurrentPromotionsAsync(): Promise<PagedList<Promotion[]> | undefined> {
    const query: PromotionQuery = {
      offset: 0,
      limit: 100,
      status: PromotionStatus.Current,
    };
    const queryString = new QueryStringHelper().toQueryString(query);
    return firstValueFrom(
      this.httpClient.get<PagedList<Promotion[]>>(this.edaraCoreBaseUrl + '/api/pos/promotions/find?' + queryString)
    ).catch(() => { return undefined; });
  }

  async findPromotionsAsync(query: any): Promise<PagedList<Promotion[]> | undefined> {
    const queryString = new QueryStringHelper().toQueryString(query);
    return firstValueFrom(
      this.httpClient.get<PagedList<Promotion[]>>(this.edaraCoreBaseUrl + '/api/pos/promotions/find?' + queryString)
    ).catch(() => { return undefined; });
  }

  deletePromotion(promotionId: string) {
    this.deletePromotionLocal(promotionId);
    return this.httpClient.delete<boolean>(this.edaraCoreBaseUrl + '/api/pos/promotions/' + promotionId);
  }

  async getCurrentPromotions(): Promise<Promotion[]> {
    let result: Promotion[] = [];
    const count = await this.countPromotionsLocalAsync();
    if (!this.onlineOfflineService.isOnline || count > 0) {
      result = await this.getCurrentPromotionsLocalAsync();
      return result;
    } else {
      const res = await this.getCurrentPromotionsAsync();
      this.addPromotionsToLocalDb(res?.items);
      return res ? res.items : [];
    }
  }

  private countPromotionsLocalAsync(): Promise<number> {
    return this.localDbProvider.db.POS_Promotions.count()
      .catch((err: any) => this.localDbProvider.handleErrors(err));
  }

  private getCurrentPromotionsLocalAsync(): Promise<Promotion[]> {
    return this.localDbProvider.db.POS_Promotions
      .filter((itm: Promotion) => new Date(itm.startDate) < new Date())
      .and((itm: Promotion) => new Date(itm.endDate) > new Date())
      .toArray();
  }

  private deletePromotionLocal(promotionId: string) {
    return this.localDbProvider.db.POS_Promotions.delete(promotionId)
      .catch((err: any) => this.localDbProvider.handleErrors(err));
  }

  async findCashRegistersAsync(query: any): Promise<PagedList<CashRegister[]> | undefined> {
    const queryString = new QueryStringHelper().toQueryString(query);
    return firstValueFrom(
      this.httpClient.get<PagedList<CashRegister[]>>(this.edaraCoreBaseUrl + '/api/pos/cashregisters/find?' + queryString)
        .pipe(tap(data => this.cacheCashRegistersImages(data)))
    ).catch(() => { return undefined; });
  }

  private async cacheCashRegistersImages(data: PagedList<CashRegister[]>) {
    if (data.items?.length > 0) {
      await Promise.all(
        data.items.filter(cashRegister => !!cashRegister.printTemplates)
          .map(cashRegister => this.cachePrintTemplatesImages(cashRegister.printTemplates))
      );
    }
    return data;
  }

  private async cachePrintTemplatesImages(printTemplates: PrintTemplateV3[]) {
    for (let index = 0; index < printTemplates.length; index++) {
      const element = printTemplates[index];
      if (element.header?.logoUrl) {
        await this.imageService.fetchImage(element.header.logoUrl)
          .then(blob => this.imageService.saveImageToDatabase(element.header!.logoUrl!, blob));
      }
    }
  }

  addCashRegistersToLocalDb(cashRegisters: CashRegister[]) {
    if (cashRegisters && cashRegisters.length > 0) {
      this.localDbProvider.db.POS_CashRegisters.bulkPut(cashRegisters)
        .catch((err: any) => this.localDbProvider.handleErrors(err));
    }
  }

  clearCashRegistersFromLocalDb() {
    this.localDbProvider.db.POS_CashRegisters.clear();
  }

  findPosProductsAsync(query: any): Promise<PagedList<Product[]> | undefined> {
    const queryString = new QueryStringHelper().toQueryString(query);
    return firstValueFrom(
      this.httpClient.get<PagedList<Product[]>>(this.edaraCoreBaseUrl + '/api/pos/Products/find?' + queryString)
    ).catch(() => { return undefined; });
  }

  addPosProductsToLocalDb(products: Product[]) {
    if (products && products.length > 0) {
      products.forEach(product => this.mapPosProduct(product));
      this.localDbProvider.db.POS_Products.bulkPut(products)
        .catch((err: any) => this.localDbProvider.handleErrors(err));
    }
  }

  private mapPosProduct(product: Product) {
    if (product.unitOfMeasures) {
      product.uom_barcodes = product.unitOfMeasures
        .map(item => item.partNumber)
        .filter((itm): itm is string => !!itm);

      product.uom_SKUs = product.unitOfMeasures
        .map(item => item.sku)
        .filter((itm): itm is string => !!itm);
    }

    if (product.variants) {
      product.variant_barcodes = product.variants.map(item => item.partNumber).filter(itm => !!itm);
      product.variant_SKUs = product.variants.map(item => item.sku).filter(itm => !!itm);
    }

    const searchKeywords = product.name.split(' ').filter(itm => !!itm);
    if (product.attributes) {
      searchKeywords.push(...flatMap(product.attributes, x => x.values));
    }
    product.searchKeywords = uniq(searchKeywords);
  }

  getUnsyncedCustomersV3Async(): Promise<CustomerV3[]> {
    return this.localDbProvider.db.POS_Customers_V3.orderBy('createdDate')
      .and((itm: CustomerV3) => !!itm.localId)
      .toArray();
  }

  syncCustomersV3ToServerAsync(customers: CustomerV3[]): Promise<CustomerSyncResult[] | undefined> {
    return firstValueFrom(
      this.httpClient.post<CustomerSyncResult[]>(this.edaraCoreBaseUrl + '/api/pos/customers/sync', customers)
    ).catch(() => { return undefined; });
  }

  saveSyncedCustomersV3ToLocalDb(syncResults: CustomerSyncResult[]) {
    for (let customerSyncResult of syncResults) {
      if (customerSyncResult.syncStatus === CustomerV3SyncStatus.Success) {
        // Delete old customer record
        this.deleteCustomerV3ByLocalIdFromLocalDb(customerSyncResult.localId);

        // Add customer to local db
        this.mapCustomerV3(customerSyncResult.customer);
        this.localDbProvider.db.POS_Customers_V3.put(customerSyncResult.customer)
          .catch((err: any) => this.localDbProvider.handleErrors(err));
      }
    }
  }

  deleteCustomerV3ByLocalIdFromLocalDb(localId: string) {
    this.localDbProvider.db.POS_Customers_V3
      .where('localId').equals(localId)
      .delete()
      .catch((err: any) => this.localDbProvider.handleErrors(err));
  }

  findCustomersV3Async(query: CustomerQuery): Promise<PagedList<CustomerV3[]> | undefined> {
    const queryString = new QueryStringHelper().toQueryString(query);
    return firstValueFrom(
      this.httpClient.get<PagedList<CustomerV3[]>>(this.edaraCoreBaseUrl + '/api/pos/Customers/find?' + queryString)
    ).catch(() => { return undefined; });
  }

  addCustomersV3ToLocalDb(customers: CustomerV3[]) {
    if (customers && customers.length > 0) {
      customers.forEach(customer => this.mapCustomerV3(customer));
      this.localDbProvider.db.POS_Customers_V3.bulkPut(customers)
        .catch((err: any) => this.localDbProvider.handleErrors(err));
    }
  }

  private mapCustomerV3(customer: CustomerV3) {
    if (customer.mobile && customer.mobile.trim().includes(' ')) {
      customer.mobileParts = customer.mobile.split(' ').filter(itm => !!itm);
    }
  }

  createSalesOrderV3(salesOrder: SalesOrderV3): Promise<SalesOrderV3 | undefined> {
    return firstValueFrom(
      this.httpClient.post<SalesOrderV3>(this.edaraCoreBaseUrl + '/api/pos/SalesOrders', salesOrder.minify())
    ).catch(() => { return undefined; });
  }

  findLocationsAsync(query: any): Promise<PagedList<PosLocation[]> | undefined> {
    const queryString = new QueryStringHelper().toQueryString(query);
    return firstValueFrom(
      this.httpClient.get<PagedList<PosLocation[]>>(this.edaraCoreBaseUrl + '/api/pos/Locations/find?' + queryString)
    ).catch(() => { return undefined; });
  }

  addLocationsToLocalDb(locations: PosLocation[]) {
    if (locations && locations.length > 0) {
      this.localDbProvider.db.POS_Locations.bulkPut(locations)
        .catch((err: any) => this.localDbProvider.handleErrors(err));
    }
  }

  findTaxesAsync(query: any): Promise<PagedList<Tax[]> | undefined> {
    const queryString = new QueryStringHelper().toQueryString(query);
    return firstValueFrom(
      this.httpClient.get<PagedList<Tax[]>>(this.edaraCoreBaseUrl + '/api/pos/taxes/find?' + queryString)
    ).catch(() => { return undefined; });
  }

  addTaxesToLocalDb(taxes: Tax[]) {
    if (taxes && taxes.length) {
      this.localDbProvider.db.POS_Taxes.bulkPut(taxes)
        .catch((err: any) => this.localDbProvider.handleErrors(err));
    }
  }

  findDeliveryZonesAsync(query: any): Promise<PagedList<DeliveryZone[]> | undefined> {
    const queryString = new QueryStringHelper().toQueryString(query);
    return firstValueFrom(
      this.httpClient.get<PagedList<DeliveryZone[]>>(this.edaraCoreBaseUrl + '/api/pos/DeliveryZones/find?' + queryString)
    ).catch(() => { return undefined; });
  }

  addDeliveryZonesToLocalDb(deliveryZones: DeliveryZone[]) {
    if (deliveryZones && deliveryZones.length > 0) {
      this.localDbProvider.db.POS_DeliveryZones.bulkPut(deliveryZones)
        .catch((err: any) => this.localDbProvider.handleErrors(err));
    }
  }

  findSalesOrderCustomFieldsAsync(query: any): Promise<PagedList<CustomField[]> | undefined> {
    const queryString = new QueryStringHelper().toQueryString(query);
    return firstValueFrom(
      this.httpClient.get<PagedList<CustomField[]>>(this.edaraCoreBaseUrl + '/api/pos/customfields?' + queryString)
    ).catch(() => { return undefined; });
  }

  addSalesOrderCustomFieldsToLocalDb(customFields: CustomField[]) {
    if (customFields && customFields.length > 0) {
      this.localDbProvider.db.POS_SO_CustomFields.bulkPut(customFields)
        .catch((err: any) => this.localDbProvider.handleErrors(err));
    }
  }
}
